import type { Editor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import { createEditorImagePolicy, safeEditorLink } from '@cdf/file-core/browser';

export { safeEditorLink };

export const MAX_EDITOR_MATCHES = 1000;

export interface FindOptions { matchCase?: boolean; wholeWord?: boolean }

/** Search text blocks as a whole so formatting boundaries do not hide matches. */
export function findDocumentMatches(editor: Editor, search: string, options: FindOptions = {}) {
  const matches: Array<{ from: number; to: number }> = [];
  let truncated = false;
  if (!search) return { matches, truncated };
  const literal = search.replace(/[.*+?^$()|[\]{}\\]/g, '\\$&');
  const source = options.wholeWord ? `(?<![\\p{L}\\p{N}_])${literal}(?![\\p{L}\\p{N}_])` : literal;
  editor.state.doc.descendants((node, position) => {
    if (truncated) return false;
    if (!node.isTextblock) return;
    const text = node.textBetween(0, node.content.size, '', '\ufffc');
    const expression = new RegExp(source, options.matchCase ? 'gu' : 'giu');
    let match = expression.exec(text);
    while (match) {
      const offset = match.index;
      if (matches.length === MAX_EDITOR_MATCHES) { truncated = true; break; }
      matches.push({ from: position + 1 + offset, to: position + 1 + offset + match[0].length });
      match = expression.exec(text);
    }
    return false;
  });
  return { matches, truncated };
}

/** Open only explicit, validated link clicks; keep the working document in place. */
export function handleDocumentLinkClick(view: EditorView, event: MouseEvent): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(anchor instanceof HTMLAnchorElement) || !view.dom.contains(anchor)) return false;
  event.preventDefault();
  const href = safeEditorLink(anchor.getAttribute('href') ?? '');
  if (href) window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}

/** This app's same-origin image routes; the shared policy adds bounded inline images and refuses everything else. */
export const safeEditorImage = createEditorImagePolicy({
  allowedPaths: [
    /^\/api\/files\/[a-zA-Z0-9-]+\/content$/,
    /^\/api\/managed-documents\/[0-9a-f-]{36}\/images\/[a-f0-9]{64}\.(?:png|jpeg|gif|webp)$/,
  ],
});
